<template>
  <div class="exercise-form-content">
    <div class="flex flex-wrap justify-end">
      <vs-input
        class="w-full name-field"
        label="Exercise name"
        v-model="localExercise.name"
        @blur="saveExercise(localExercise)"
      />
    </div>
    <div class="w-full mt-10">
      <label class="vs-input--label">Slug</label>
      <vx-input-group
        class="mb-base"
        prependClasses="border border-solid border-grey border-r-0"
      >
        <template slot="prepend">
          <div class="prepend-text">
            <vs-switch
              v-model="autoAlias"
              :checked="autoAlias"
              class="auto-alias-checkbox"
            />
          </div>
        </template>

        <vs-input
          v-model="localExercise.slug"
          :disabled="autoAlias"
          class="auto-alias-field"
          @blur="saveExercise(localExercise)"
        />
      </vx-input-group>
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <vs-input
        class="w-full duration-field"
        label="Duration"
        v-model="duration"
        readonly
      />
    </div>
    <div class="flex">
      <div class="w-1/2 pr-5 md:mt-10 mt-6">
        <label for="">Language</label>
        <v-select
          class="bg-white"
          :clearable="false"
          placeholder="Select a language"
          :options="languageOptions"
          v-model="localExercise.language"
          @input="saveExercise(localExercise)"
        />
      </div>
    </div>
    <div class="md:mt-10 mt-6">
      <label class="vs-input--label">Instructor</label>
      <v-select
        v-model="localExercise.instructor_id"
        :reduce="(instructor) => instructor.value"
        placeholder="Select an instructor"
        :options="getInstructors"
        @input="saveExercise(localExercise)"
      />
    </div>
    <div class="edit-description-group w-full mt-10 flex flex-wrap justify-end">
      <label class="w-full" for="">Description</label>
      <markdown-editor
        :id="localExercise.id"
        class="w-full description-field"
        ref="markdownEditor"
        v-model="localExercise.description"
        :options="{
          hideModeSwitch: true,
          previewStyle: 'tab',
          events: { blur: saveExerciseDescription },
        }"
        height="300px"
        @input="saveExercise(localExercise)"
      />
    </div>
    <vs-tabs class="mt-10" alignment="fixed">
      <vs-tab class="pt-8" label="Starter code">
        <div>
          <MonacoEditor
            class="pt-5 monaco-editor"
            theme="vs-dark"
            height="500"
            :language="localExercise.language"
            v-model="starterCode"
          ></MonacoEditor>
        </div>
      </vs-tab>
      <vs-tab v-if="isEditing" class="pt-8" label="Tests">
        <div>
          <ExerciseTestsForm :exercise="localExercise" />
        </div>
      </vs-tab>
      <vs-tab v-if="isEditing" class="pt-8" label="Solutions">
        <div>
          <ExerciseSolutionsForm :exercise="localExercise" />
        </div>
      </vs-tab>
      <vs-tab v-if="isEditing" class="pt-8" label="Files">
        <div>
          <vs-alert
            color="warning"
            icon-pack="feather"
            icon="icon-info"
            class="w-full"
            v-if="rememberToSaveFiles"
          >
            Please remember to save the Exercise after add or delete a file.
          </vs-alert>
          <small
            >If you'd like to have something else installed in your exercise
            environment, upload a <strong>requirements.txt</strong> file</small
          >
          <FilesAttachments
            type="exercise"
            :files="getCurrentExerciseFiles"
            :loading="filesLoading"
            :content="getCurrentExercise"
            @file-deleted="deleteFileFromExercise"
            @file-added="addFileToExercise"
            @file-changed="fileChanged"
          />
        </div>
      </vs-tab>
    </vs-tabs>
    <div class="flex md:mt-10 mt-6 -mx-2 action-buttons" v-if="embedded">
      <vs-button
        class="w-full mx-2 save-button"
        color="primary"
        icon-pack="feather"
        @click.stop="saveAndClick"
        >Save</vs-button
      >
    </div>
  </div>
</template>
<script>
import slug from 'slug-generator'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import MonacoEditor from 'monaco-editor-vue'
import vSelect from 'vue-select'
import cloneDeep from 'lodash.clonedeep'
import MarkdownEditor from '@/components/markdownEditor'
import FilesAttachments from '@/components/FilesEngineAttachments'
import ExerciseTestsForm from './ExerciseTestsForm'
import ExerciseSolutionsForm from './ExerciseSolutionsForm'
import { HmsToSeconds, secondsToHms } from '@/utils/utils.js'
import { warningUnsaved } from '@/utils/mixins'

import '@/assets/css/prism-theme.css'
import 'prismjs/components/prism-python'

export default {
  mixins: [warningUnsaved],
  props: {
    embedded: Boolean,
    onClick: {
      type: Function,
      required: true,
    },
  },
  components: {
    MarkdownEditor,
    MonacoEditor,
    'v-select': vSelect,
    FilesAttachments,
    ExerciseTestsForm,
    ExerciseSolutionsForm,
  },
  data() {
    return {
      autoAlias: true,
      localExercise: {},
      languageOptions: ['python'],
      rememberToSave: false,
      filesLoading: false,
      duration: '00:00:00',
      durationInSeconds: '0',
    }
  },
  watch: {
    'localExercise.name': function (newValue) {
      if (this.autoAlias) {
        this.localExercise.slug = slug(newValue)
      }
    },
    autoAlias: function (newValue) {
      if (newValue) {
        this.localExercise.slug = slug(this.localExercise.name)
      }
    },
    getCurrentExercise: {
      immediate: true,
      handler(newValue) {
        this.localExercise = cloneDeep(newValue)
        this.durationInSeconds = this.localExercise.duration_in_seconds
        this.duration = secondsToHms(this.durationInSeconds)
      },
    },
    duration: function (newValue) {
      this.durationInSeconds = HmsToSeconds(newValue)
      this.transformAndSaveExercise()
    },
    'getCurrentExercise.id': {
      immediate: true,
      async handler() {
        try {
          if (this.getCurrentExercise.id) {
            // Get Exercise tests
            await this.getAllExerciseTests({
              exerciseId: this.localExercise.id,
            })
            await this.getAllExerciseSolutions({
              exerciseId: this.localExercise.id,
            })
          }
        } catch (error) {
          this.showError(error)
        }
      },
    },
    'localExercise.files': async function () {
      if (
        JSON.stringify(this.localExercise.files) ===
        JSON.stringify(this.getCurrentExercise.files)
      ) {
        this.rememberToSaveFiles = false
      }
      if (this.localExercise.files && this.localExercise.files.length) {
        this.filesLoading = true
        await this.getAllExerciseFiles(this.localExercise.files)
        this.filesLoading = false
      }
      if (this.localExercise.files && !this.localExercise.files.length) {
        this.resetFilesInExercise()
      }
    },
    rememberToSaveFiles: function (newValue) {
      this.$emit('prevent-close', newValue)
    },
  },
  computed: {
    ...mapGetters('exercises', [
      'getCurrentExercise',
      'getCurrentExerciseFiles',
    ]),
    ...mapGetters('instructors', ['getInstructorsEntries', 'getInstructors']),
    isEditing() {
      return !!this.getCurrentExercise.id
    },
    starterCode: {
      get() {
        return this.localExercise.starter_code
      },
      set(value) {
        this.saveStarterCode(value)
        this.localExercise.starter_code = value
      },
    },
  },
  methods: {
    ...mapMutations('exercises', {
      saveExercise: 'STORE_CURRENT_EXERCISE',
      saveStarterCode: 'STORE_CURRENT_EXERCISE_CODE',
      changeFileInExercise: 'CHANGE_FILE_IN_EXERCISE',
      resetFilesInExercise: 'RESET_FILES_IN_EXERCISE',
    }),
    ...mapActions('exercises', [
      'getAllExerciseFiles',
      'getAllExerciseTests',
      'getAllExerciseSolutions',
    ]),
    ...mapActions('instructors', ['getAllInstructors']),
    saveAndClick() {
      this.saveExercise(this.localExercise)
      this.onClick()
    },
    saveExerciseIfNew() {
      if (!this.isEditing) {
        this.saveExercise(this.localExercise)
      }
    },
    saveExerciseDescription() {
      this.saveExercise(this.localExercise)
    },
    deleteFileFromExercise(file) {
      const index = this.localExercise.files.indexOf(file.id)
      if (index > -1) {
        this.localExercise.files.splice(index, 1)
      }
      this.rememberToSaveFiles = true
    },
    addFileToExercise(file) {
      this.localExercise.files = [...this.localExercise.files, file.id]
      this.rememberToSaveFiles = true
    },
    fileChanged(payload) {
      this.changeFileInExercise(payload)
    },
    transformAndSaveExercise() {
      this.localExercise.duration_in_seconds = this.durationInSeconds
      this.saveExercise(this.localExercise)
    },
  },
  async mounted() {
    this.localExercise = cloneDeep(this.getCurrentExercise)
    try {
      if (!this.embedded && !Object.keys(this.getInstructorsEntries).length) {
        await this.getAllInstructors()
      }
    } catch (error) {
      this.showError(error)
    }
  },
}
</script>
